import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { UseMutationResult } from '@tanstack/react-query'

import {
    Card,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    Chip,
    IconButton,
} from '@material-ui/core'
import {
    Mail,
    Phone,
    Link as LinkIcon,
    CheckCircle,
    Cancel as XCircle,
    ArrowForward as ArrowForwardIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    Prospect,
    DetailedCampaignProspect,
    Campaign,
    CampaignStepType,
} from '../../../../models/Outreach/types'
import {
    ProspectOrCampaignProspect,
    isDetailedCampaignProspect,
} from '../../../../hooks/Outreach/useLeads'
import { getPipelineStage, getStatusColor, getEnrichmentColor } from '../utils'
import { useCampaign } from '../../../../hooks/Outreach/useCampaign'
import { Routes } from '../../../../navigation/config/routes'
import { ConvertToLeadDialog } from './ConvertToLeadDialog'
import { CampaignMenu } from '../../ProspectList/ProspectListDetail/CampaignMenu'
import { CampaignEmailUnsubscribeIndicator } from '../../CampaignEmailUnsubscribeIndicator'

interface ProspectsTableProps {
    combinedProspects: ProspectOrCampaignProspect[]
    pagination: {
        count: number
        next: string | null
        previous: string | null
        currentPage: number
        pageSize: number
        totalPages: number
    }
    isLoading: boolean
    onRowClick: (
        campaignProspect: DetailedCampaignProspect | null,
        prospect: Prospect | null,
    ) => void
    onPageChange: (event: unknown, newPage: number) => void
    onRowsPerPageChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void
    addProspectToCampaign: UseMutationResult<
        any,
        any,
        {
            prospectId: string | number
            campaignId: string | number
        },
        any
    >
}

const useStyles = makeStyles((theme: Theme) => ({
    tableContainer: {
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 400px)', // Adjust based on your header/stats height
    },
    scrollableTableContainer: {
        overflowX: 'auto',
        overflowY: 'auto',
        flex: 1,
    },
    table: {
        '& .MuiTableCell-root': {
            borderBottom: '1px solid #e2e8f0',
        },
    },
    tableHead: {
        backgroundColor: '#f8fafc',
        '& .MuiTableCell-root': {
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: '0.875rem',
        },
    },
    tableRow: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#f8fafc',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
        },
    },
    tableCell: {
        padding: theme.spacing(2),
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    contactCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    enrichmentIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    pipelineBadge: {
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
    },
    successChip: {
        backgroundColor: '#10b981',
        color: 'white',
        '& .MuiChip-icon': {
            color: 'white',
        },
    },
    outlineChip: {
        borderColor: '#6b7280',
        color: '#6b7280',
        '& .MuiChip-icon': {
            color: '#6b7280',
        },
    },
}))

export const ProspectsTable: React.FC<ProspectsTableProps> = ({
    combinedProspects,
    pagination,
    isLoading,
    onRowClick,
    onPageChange,
    onRowsPerPageChange,
    addProspectToCampaign,
}) => {
    const classes = useStyles()
    const history = useHistory()

    // Campaign menu state
    const [campaignMenuAnchor, setCampaignMenuAnchor] = useState<{
        element: HTMLElement
        prospectId: number
        prospect: Prospect
    } | null>(null)

    // Convert to Lead dialog state
    const [convertDialogOpen, setConvertDialogOpen] = useState(false)
    const [
        selectedProspectOrCampaignProspect,
        setSelectedProspectOrCampaignProspect,
    ] = useState<ProspectOrCampaignProspect | undefined>(undefined)

    // Fetch campaigns
    const { campaigns, isLoadingCampaigns } = useCampaign()

    // Campaign menu handlers
    const handleCampaignMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        prospect: Prospect,
    ) => {
        event.stopPropagation() // Prevent row click
        setCampaignMenuAnchor({
            element: event.currentTarget,
            prospectId: prospect.id,
            prospect,
        })
    }

    const handleCampaignMenuClose = () => {
        setCampaignMenuAnchor(null)
    }

    const handleSelectCampaign = (campaign: Campaign, prospectId: number) => {
        addProspectToCampaign.mutate(
            {
                prospectId,
                campaignId: campaign.id,
            },
            {
                onSuccess: () => {
                    handleCampaignMenuClose()
                },
            },
        )
    }

    const handleCreateCampaign = () => {
        handleCampaignMenuClose()
        history.push(Routes.manage.campaign.create)
    }

    // Validation helpers for campaign assignment
    const campaignRequiresEmail = (campaign: Campaign): boolean => {
        return campaign.steps.some(
            (step) => step.step_type === CampaignStepType.EMAIL,
        )
    }

    const campaignRequiresTexting = (campaign: Campaign): boolean => {
        return campaign.steps.some(
            (step) => step.step_type === CampaignStepType.TEXT_MESSAGE,
        )
    }

    const canProspectBeAddedToCampaign = (
        prospect: Prospect,
        campaign: Campaign,
    ): { valid: boolean; reason?: string } => {
        const requiresEmail = campaignRequiresEmail(campaign)
        const requiresTexting = campaignRequiresTexting(campaign)

        if (requiresEmail && !prospect.email?.value) {
            return {
                valid: false,
                reason:
                    'This campaign requires email, but prospect has no email address',
            }
        }

        if (requiresTexting && !prospect.phone?.value) {
            return {
                valid: false,
                reason:
                    'This campaign requires texting, but prospect has no phone number',
            }
        }

        return { valid: true }
    }

    const renderEnrichmentIndicators = (prospect: Prospect) => {
        const emailColor = getEnrichmentColor(
            !!prospect.email.value,
            !!prospect.email.last_attempted,
        )
        const phoneColor = getEnrichmentColor(
            !!prospect.phone.value,
            !!prospect.phone.last_attempted,
        )
        const profileColor = getEnrichmentColor(
            !!prospect.linkedinProfile.value,
            !!prospect.linkedinProfile.last_attempted,
        )

        return (
            <Box className={classes.enrichmentIndicator}>
                <Mail style={{ fontSize: 16, color: emailColor }} />
                <Phone style={{ fontSize: 16, color: phoneColor }} />
                <LinkIcon style={{ fontSize: 16, color: profileColor }} />
            </Box>
        )
    }

    if (isLoading) {
        return (
            <Card className={classes.tableContainer}>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="400px"
                >
                    <CircularProgress />
                </Box>
            </Card>
        )
    }

    return (
        <Card className={classes.tableContainer}>
            <TableContainer className={classes.scrollableTableContainer}>
                <Table className={classes.table} style={{ width: '100%' }}>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.tableCell}>
                                Name
                            </TableCell>

                            <TableCell className={classes.tableCell}>
                                Enrichment
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                Active Campaign
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                Last Contacted Date
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                Convert to Lead
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {combinedProspects.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className={classes.tableCell}
                                >
                                    <Typography
                                        variant="body2"
                                        align="center"
                                        style={{ color: '#9ca3af' }}
                                    >
                                        No prospects found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            combinedProspects.map((item) => {
                                const isCampaignProspect = isDetailedCampaignProspect(
                                    item,
                                )
                                const prospect = isCampaignProspect
                                    ? item.prospect
                                    : item
                                const campaignProspect = isCampaignProspect
                                    ? item
                                    : null
                                const pipelineStage = campaignProspect
                                    ? getPipelineStage(campaignProspect)
                                    : null
                                const statusColor = pipelineStage
                                    ? getStatusColor(pipelineStage)
                                    : null
                                const hasActiveCampaign =
                                    isCampaignProspect ||
                                    prospect.active_campaign !== null

                                return (
                                    <TableRow
                                        key={
                                            isCampaignProspect &&
                                            campaignProspect
                                                ? `campaign-${campaignProspect.id}`
                                                : `prospect-${prospect.id}`
                                        }
                                        className={classes.tableRow}
                                        onClick={() => {
                                            onRowClick(
                                                isCampaignProspect &&
                                                    campaignProspect
                                                    ? campaignProspect
                                                    : null,
                                                prospect,
                                            )
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {/* Name */}
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            <Box
                                                className={classes.nameCell}
                                                style={{
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    gap: 6,
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    {prospect.name}
                                                </Typography>
                                                <CampaignEmailUnsubscribeIndicator
                                                    prospect={prospect}
                                                />
                                            </Box>
                                        </TableCell>

                                        {/* Enrichment */}
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            {renderEnrichmentIndicators(
                                                prospect,
                                            )}
                                        </TableCell>

                                        {/* Campaign */}
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            {hasActiveCampaign ? (
                                                <Chip
                                                    icon={
                                                        <CheckCircle
                                                            style={{
                                                                height: 12,
                                                                width: 12,
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        isCampaignProspect
                                                            ? campaignProspect!
                                                                  .campaign.name
                                                            : prospect
                                                                  .active_campaign
                                                                  ?.name
                                                    }
                                                    className={
                                                        classes.successChip
                                                    }
                                                    size="small"
                                                />
                                            ) : (
                                                <Chip
                                                    icon={
                                                        <XCircle
                                                            style={{
                                                                height: 12,
                                                                width: 12,
                                                            }}
                                                        />
                                                    }
                                                    label="Not in Campaign"
                                                    variant="outlined"
                                                    className={
                                                        classes.outlineChip
                                                    }
                                                    size="small"
                                                    onClick={(e) =>
                                                        handleCampaignMenuOpen(
                                                            e,
                                                            prospect,
                                                        )
                                                    }
                                                    style={{
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            {campaignProspect?.last_step_sent_at ? (
                                                <Typography variant="body2">
                                                    {new Date(
                                                        campaignProspect.last_step_sent_at,
                                                    ).toLocaleDateString(
                                                        'en-US',
                                                        {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: 'numeric',
                                                        },
                                                    )}
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: '#9ca3af' }}
                                                >
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setSelectedProspectOrCampaignProspect(
                                                        item,
                                                    )
                                                    setConvertDialogOpen(true)
                                                }}
                                                style={{
                                                    color: '#3b82f6',
                                                }}
                                            >
                                                <ArrowForwardIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {/* Calculate valid page number (0-indexed for TablePagination) */}
            {(() => {
                if (!pagination || pagination.count === 0) {
                    return null
                }
                const maxPage = Math.max(
                    0,
                    Math.ceil(pagination.count / pagination.pageSize) - 1,
                )
                const validPage = Math.max(
                    0,
                    Math.min(pagination.currentPage - 1, maxPage),
                )

                return (
                    <TablePagination
                        component="div"
                        count={pagination.count}
                        page={validPage}
                        onChangePage={onPageChange}
                        rowsPerPage={pagination.pageSize}
                        onChangeRowsPerPage={onRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                )
            })()}
            {/* Campaign menu component */}
            {campaignMenuAnchor && (
                <CampaignMenu
                    anchorEl={campaignMenuAnchor.element}
                    open={!!campaignMenuAnchor}
                    onClose={handleCampaignMenuClose}
                    prospect={campaignMenuAnchor.prospect}
                    campaigns={campaigns}
                    isLoadingCampaigns={isLoadingCampaigns}
                    onSelectCampaign={handleSelectCampaign}
                    onCreateCampaign={handleCreateCampaign}
                    canProspectBeAddedToCampaign={canProspectBeAddedToCampaign}
                    isAddingToCampaign={addProspectToCampaign.isLoading}
                />
            )}
            <ConvertToLeadDialog
                open={convertDialogOpen}
                onClose={() => {
                    setConvertDialogOpen(false)
                    setSelectedProspectOrCampaignProspect(undefined)
                }}
                prospectOrCampaignProspect={selectedProspectOrCampaignProspect}
            />
        </Card>
    )
}
